import { UnprocessableEntityError } from "@exceptions";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { UseFormSetError } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

export function useLanguage() {
  const {
    i18n: { language },
  } = useTranslation();

  return language;
}

export function useSetFormError(
  errorKey: string,
  setError: UseFormSetError<any>,
) {
  const queryClient = useQueryClient();
  const error = queryClient.getQueryData(["ERROR", errorKey]);

  if (error instanceof UnprocessableEntityError) {
    const responseBody = error.responseBody;
    if (typeof responseBody === "string") {
      return;
    }

    for (const [fieldName, fieldErrors] of Object.entries(
      responseBody.errors ?? {},
    )) {
      const errorMessage = fieldErrors.join(", ");

      setError(fieldName, {
        type: "server",
        message: errorMessage,
      });
    }

    queryClient.removeQueries({ queryKey: ["ERROR", errorKey] });
  }
}

/**
 * Custom hook to manage a value in localStorage.
 *
 * @param key - The key under which the value is stored in localStorage.
 * @param defaultValue - The default value to use if no value is found in localStorage.
 * @returns A tuple containing the current value and a function to update the value.
 *
 * @example
 * const [value, setValue] = useLocalstorage('myKey', 'default');
 * setValue('newValue');
 */
export function useLocalstorage(
  key: string,
  defaultValue: string | null = null,
): [string | null, (value: string | null) => void] {
  const [value, setValue] = useState(() => {
    const storedValue = localStorage.getItem(key);
    return storedValue ?? defaultValue;
  });

  const setStoredValue = (newValue: string | null) => {
    setValue(newValue);

    if (newValue === null) {
      localStorage.removeItem(key);
      return;
    }

    localStorage.setItem(key, newValue);
  };

  return [value, setStoredValue];
}

export function useGetSearchParam(key: string): string | null {
  const [searchParams] = useSearchParams();

  return searchParams.get(key);
}

/**
 * Custom hook that fixes an issue with Radix Dialog where the body is not clickable after closing the dialog.
 *
 * @param open - A boolean indicating whether the Radix Dialog is open or not.
 *
 * @remarks
 * This hook sets a timeout to reset the `pointerEvents` style of the document body when the dialog is closed.
 * The issue is documented here: https://github.com/shadcn-ui/ui/issues/1912
 */
export function useCleanupAfterRadix(open: boolean) {
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!open) {
        document.body.style.pointerEvents = "";
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [open]);
}

/**
 * Custom hook to get the current page width.
 *
 * @returns The current width of the page.
 */
export function usePageWidth(): number {
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return width;
}
